import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, Users, TrendingDown, GraduationCap, Clock, Percent, Pencil, Check, X, Loader2, Inbox } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import SubTabs from "@/components/SubTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Hjælpe-konstanter ----------
const STATUS_STYLES = {
  "Aktiv":                { bg: "bg-green-500/10",  text: "text-green-600 dark:text-green-400",  dot: "bg-green-500" },
  "Tilmeldt Uddannelse":  { bg: "bg-green-500/10",  text: "text-green-600 dark:text-green-400",  dot: "bg-green-500" },
  "Orlov":                { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", dot: "bg-purple-500" },
  "Pause":                { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", dot: "bg-purple-500" },
  "Stoppet før tid":      { bg: "bg-red-500/10",    text: "text-red-600 dark:text-red-400",       dot: "bg-red-500" },
  "Stoppet":              { bg: "bg-red-500/10",    text: "text-red-600 dark:text-red-400",       dot: "bg-red-500" },
  "Starter senere":       { bg: "bg-blue-500/10",   text: "text-blue-600 dark:text-blue-400",     dot: "bg-blue-500" },
  "Afsluttet uddannelse": { bg: "bg-yellow-500/10", text: "text-yellow-600 dark:text-yellow-400", dot: "bg-yellow-500" },
  "Afsluttet":            { bg: "bg-yellow-500/10", text: "text-yellow-600 dark:text-yellow-400", dot: "bg-yellow-500" },
};

const ALLE_STATUSER = [
  "Aktiv", "Tilmeldt Uddannelse", "Starter senere", "Orlov", "Pause",
  "Stoppet før tid", "Stoppet", "Afsluttet uddannelse", "Afsluttet",
  "Emne i dialog", "Emne afsluttet", "Emne", "Klar til kontrakt",
];
// "Tilmeldt Uddannelse"-gruppen: aktive elever der ikke er afsluttede/stoppede
const TILMELDT_STATUSER = ["Aktiv", "Tilmeldt Uddannelse", "Starter senere", "Orlov", "Pause"];
const AKTIV_STATUSER = ["Aktiv", "Tilmeldt Uddannelse"];

const FILTER_GROUPS = [
  { id: "Tilmeldt Uddannelse", label: "Tilmeldt udd." },
  { id: "Aktiv", label: "Aktiv" },
  { id: "Starter senere", label: "Starter senere" },
  { id: "Orlov", label: "Orlov" },
  { id: "Afsluttet uddannelse", label: "Afsluttet udd." },
  { id: "Stoppet før tid", label: "Stoppet før tid" },
  { id: "Emne i dialog", label: "Emne i dialog" },
  { id: "Emne afsluttet", label: "Emne afsluttet" },
  { id: "Emne", label: "Emne" },
  { id: "Klar til kontrakt", label: "Klar til kontrakt" },
  { id: "alle", label: "Alle" },
];

const BYER = [
  { id: "alle", label: "Alle" },
  { id: "Koebenhavn", label: "København" },
  { id: "Aarhus", label: "Aarhus" },
  { id: "Odense", label: "Odense" },
  { id: "Aalborg", label: "Aalborg" },
  { id: "Kolding", label: "Kolding" },
];

const DISPLAY_NAMES = { "Koebenhavn": "København" };
const displayCity = (c) => DISPLAY_NAMES[c] || c || "—";

const MONTHS_DA = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

function fmtDate(d) {
  if (!d) return "—";
  try {
    const [y, m] = d.split("-");
    return `${MONTHS_DA[parseInt(m) - 1]} ${y}`;
  } catch { return d; }
}

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(n) + " kr.";
}

const QUICK_PERIODS = [
  { label: "Alt", fra: null, til: null },
  { label: "I år",        fra: () => `${new Date().getFullYear()}-01-01`, til: null },
  { label: "Sidste år",   fra: () => `${new Date().getFullYear() - 1}-01-01`, til: () => `${new Date().getFullYear() - 1}-12-31` },
  { label: "3 mdr.",      fra: () => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().slice(0, 10); }, til: null },
  { label: "6 mdr.",      fra: () => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d.toISOString().slice(0, 10); }, til: null },
  { label: "12 mdr.",     fra: () => { const d = new Date(); d.setMonth(d.getMonth() - 12); return d.toISOString().slice(0, 10); }, til: null },
];

// Matcher elev.afdeling mod et by-id (med Koebenhavn↔København alias)
function matchCity(afdeling, byId) {
  if (!byId || byId === "alle") return true;
  // Elever uden afdeling: vis kun under "Alle"
  if (!afdeling) return false;
  const a = String(afdeling).toLowerCase().trim();
  const b = String(byId).toLowerCase().trim();
  if (a === b) return true;
  // Alias: Koebenhavn ↔ København
  if ((a === "koebenhavn" || a === "københavn") && (b === "koebenhavn" || b === "københavn")) return true;
  return false;
}

// Tæller hvor mange der ville matche et statusfilter (case-insensitive på elev_status)
function matchStatus(elevStatus, filterId) {
  if (!filterId || filterId === "alle") return true;
  return String(elevStatus || "").toLowerCase() === String(filterId).toLowerCase();
}

// ---------- Sub-komponenter ----------
function KpiCard({ icon, label, value, sub, onClick, active }) {
  const base = "rounded-xl border border-border bg-card p-3 sm:p-4 flex flex-col gap-0.5 sm:gap-1";
  const clickable = onClick ? " cursor-pointer hover:border-[#FF2A6D]/60 transition-colors" : "";
  const activeClass = active ? " border-[#FF2A6D] ring-1 ring-[#FF2A6D]/30" : "";
  return (
    <div className={base + clickable + activeClass} onClick={onClick}>
      <div className="flex items-center gap-1.5 text-muted-foreground text-[12px] sm:text-[13px] font-medium mb-0.5 sm:mb-1 min-w-0">
        <span className="shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-foreground">{value}</div>
      {sub && <div className="text-[12px] sm:text-[13px] text-muted-foreground leading-snug">{sub}</div>}
    </div>
  );
}

function BudgetEditor({ kanal, current, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current ?? "");

  function handleSave() {
    const n = parseFloat(String(val).replace(/[^\d]/g, ""));
    if (!isNaN(n)) onSave(n);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          className="w-24 px-2 py-0.5 text-[13px] rounded border border-border bg-background text-foreground"
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
        />
        <button onClick={handleSave} className="p-0.5 text-emerald-400 hover:text-emerald-300"><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => setEditing(false)} className="p-0.5 text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  return (
    <button
      onClick={() => { setVal(current ?? ""); setEditing(true); }}
      className="flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground transition-colors group"
    >
      {current != null ? fmt(current) : <span className="italic text-muted-foreground/60">Sæt budget</span>}
      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// ---------- Hovedkomponent ----------
export default function ElevereSamletPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab- og delt by-filter
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return t === "rekruttering" ? "rekruttering" : "liste";
  });
  const [byFilter, setByFilter] = useState("alle");

  // --- Elev-liste state ---
  const [elever, setElever] = useState([]);
  const [eleverLoading, setEleverLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("Tilmeldt Uddannelse");
  const [search, setSearch] = useState("");
  const [visAntal, setVisAntal] = useState(250); // paginering: vis først 250, "vis flere" øger
  const [savingId, setSavingId] = useState(null);
  // --- Counts state (let endpoint til KPI-kasser, hentes parallelt) ---
  const [countsData, setCountsData] = useState(null);

  // --- Rekrutterings-state ---
  const [rekrutData, setRekrutData] = useState(null);
  const [rekrutLoading, setRekrutLoading] = useState(true);
  const [rekrutError, setRekrutError] = useState(null);
  const [fra, setFra] = useState("");
  const [til, setTil] = useState("");
  const [activeQuick, setActiveQuick] = useState("Alt");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ---------- Hentning ----------
  const loadRekrut = useCallback((fraVal, tilVal) => {
    setRekrutLoading(true);
    setRekrutError(null);
    const params = {};
    if (fraVal) params.fra = fraVal;
    if (tilVal) params.til = tilVal;
    return axios.get(`${API}/admin/elever/leads`, { headers, params })
      .then(r => setRekrutData(r.data))
      .catch(err => {
        if (err.response?.status === 401) logout();
        else setRekrutError(err.response?.data?.detail || "Fejl ved hentning");
      })
      .finally(() => setRekrutLoading(false));
  }, [headers, logout]);

  const loadElever = useCallback(() => {
    setEleverLoading(true);
    return axios.get(`${API}/admin/elever/ikke-aktive`, { headers })
      .then(r => setElever(r.data.elever || []))
      .catch(err => {
        if (err.response?.status === 401) logout();
      })
      .finally(() => setEleverLoading(false));
  }, [headers, logout]);

  // Let count-endpoint — kun til KPI-kasser. Returnerer hurtigt så bokse vises
  // før den tunge elev-liste er færdig.
  const loadCounts = useCallback(() => {
    return axios.get(`${API}/admin/elever/counts`, { headers })
      .then(r => setCountsData(r.data))
      .catch(err => {
        if (err.response?.status === 401) logout();
      });
  }, [headers, logout]);

  useEffect(() => {
    // Adgang: admin/superadmin/owner ELLER medarbejder med kan_se_elever-rettighed
    const isAdminRole = user && ["admin", "superadmin", "owner", "api_admin"].includes(user.role);
    const kanSeElever = user?.rettigheder?.kan_se_elever === true;
    if (!user || (!isAdminRole && !kanSeElever)) {
      navigate("/", { replace: true });
      return;
    }
    // Counts og rekrut hentes parallelt og lyn-hurtigt (let payload).
    // Elev-listen (~12.000 rækker) hentes parallelt men kan tage et øjeblik længere.
    loadCounts();
    loadRekrut("", "");
    loadElever();
  }, [user, navigate, loadCounts, loadElever, loadRekrut]);

  // ---------- Actions ----------
  const handleLogout = () => { logout(); navigate("/login"); };

  const handleImpersonate = async (navn) => {
    try {
      const res = await fetch(`${API}/auth/impersonate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ student_name: navn }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { token: impToken } = await res.json();
      navigate("/", { state: { impersonatedStudent: { name: navn, token: impToken } } });
    } catch (err) {
      alert(`Kunne ikke åbne elev-visning: ${err.message}`);
    }
  };

  const handleStatusChange = async (appItemId, newStatus) => {
    setSavingId(appItemId);
    try {
      await axios.put(`${API}/admin/elev/stamdata/${appItemId}`, { elev_status: newStatus }, { headers });
      setElever(prev => prev.map(e =>
        e.app_item_id === appItemId ? { ...e, elev_status: newStatus } : e
      ));
    } catch { /* ignore */ }
    setSavingId(null);
  };

  function applyQuick(p) {
    setActiveQuick(p.label);
    const f = p.fra ? p.fra() : "";
    const t = p.til ? p.til() : "";
    setFra(f);
    setTil(t);
    loadRekrut(f, t);
  }

  function applyCustom() {
    setActiveQuick("");
    loadRekrut(fra, til);
  }

  async function saveBudget(kanal, budget_mdr) {
    try {
      await axios.put(`${API}/admin/elever/kanal-budget`, { kanal, budget_mdr }, { headers });
      setRekrutData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          kanaler: prev.kanaler.map(k =>
            k.kanal === kanal
              ? {
                  ...k,
                  budget_mdr,
                  leadpris: k.total > 0 ? Math.round(budget_mdr / k.total) : null,
                  tilmeldt_pris: k.tilmeldte > 0 ? Math.round(budget_mdr / k.tilmeldte) : null,
                }
              : k
          ),
        };
      });
    } catch {
      // silent
    }
  }

  // ---------- Afledte data ----------

  // Elever filtreret på by (til KPI-tæller + tabel)
  const eleverByFiltered = useMemo(
    () => elever.filter(e => matchCity(e.afdeling, byFilter)),
    [elever, byFilter]
  );

  // Helper: sum værdier i et city-map matchende byFilter
  const sumCityMap = useCallback((cityMap) => {
    if (!cityMap) return 0;
    if (byFilter === "alle") return Object.values(cityMap).reduce((s, n) => s + n, 0);
    return Object.entries(cityMap).reduce((s, [city, n]) => s + (matchCity(city, byFilter) ? n : 0), 0);
  }, [byFilter]);

  // KPI: Aktive elever — fra countsData hvis tilgængelig, ellers fra eleverByFiltered
  const aktiveCount = useMemo(() => {
    if (countsData?.by_city?.aktive) return sumCityMap(countsData.by_city.aktive);
    return eleverByFiltered.filter(e => AKTIV_STATUSER.includes(e.elev_status)).length;
  }, [countsData, sumCityMap, eleverByFiltered]);

  // KPI: Starter snart
  const starterSnartCount = useMemo(() => {
    if (countsData?.by_city?.starter_senere) return sumCityMap(countsData.by_city.starter_senere);
    return eleverByFiltered.filter(e => e.elev_status === "Starter senere").length;
  }, [countsData, sumCityMap, eleverByFiltered]);

  const naesteHoldstart = useMemo(() => {
    if (countsData?.naeste_holdstart) return countsData.naeste_holdstart;
    const today = new Date().toISOString().slice(0, 10);
    const future = eleverByFiltered
      .filter(e => e.elev_status === "Starter senere" && e.start_date && e.start_date >= today)
      .map(e => e.start_date)
      .sort();
    return future.length > 0 ? future[0] : null;
  }, [eleverByFiltered]);

  // KPI: Pipeline (leads) + konvertering — ikke by-opdelt
  const totalLeads = rekrutData?.leads_total ?? 0;
  const totalTilmeldte = rekrutData?.tilmeldte_total ?? 0;
  const konvPct = totalLeads > 0 ? (totalTilmeldte / totalLeads * 100) : null;

  // Periodelabel for konvertering-subtekst
  const periodeLabel = useMemo(() => {
    if (activeQuick) return activeQuick.toLowerCase();
    if (rekrutData?.fra) return `${rekrutData.fra} → ${rekrutData.til || "i dag"}`;
    return "perioden";
  }, [activeQuick, rekrutData]);

  // Statusfilter-counts (på by-filtrerede elever) — case-insensitive
  const counts = useMemo(() => {
    const tilmeldtLower = TILMELDT_STATUSER.map(s => s.toLowerCase());
    const aktivLower = AKTIV_STATUSER.map(s => s.toLowerCase());
    const result = {
      alle: eleverByFiltered.length,
      // "Tilmeldt Uddannelse"-filteret er en gruppe (Aktiv + Tilmeldt + Starter senere + Orlov)
      "Tilmeldt Uddannelse": eleverByFiltered.filter(e => tilmeldtLower.includes(String(e.elev_status || "").toLowerCase())).length,
      // "Aktiv"-filteret matcher KPI-definitionen: Aktiv ELLER Tilmeldt Uddannelse
      "Aktiv": eleverByFiltered.filter(e => aktivLower.includes(String(e.elev_status || "").toLowerCase())).length,
    };
    ALLE_STATUSER.forEach(s => {
      if (s === "Tilmeldt Uddannelse" || s === "Aktiv") return; // håndteret ovenfor som gruppe-tællere
      result[s] = eleverByFiltered.filter(e => matchStatus(e.elev_status, s)).length;
    });
    return result;
  }, [eleverByFiltered]);

  // Fuld filtreret elev-liste (by + status + søg) — case-insensitive på status
  const eleverFiltered = useMemo(() => {
    const tilmeldtLower = TILMELDT_STATUSER.map(s => s.toLowerCase());
    const aktivLower = AKTIV_STATUSER.map(s => s.toLowerCase());
    const eksamenMaaned = countsData?.til_eksamen_naeste?.maaned ?? null;
    return eleverByFiltered.filter(e => {
      const statusLower = String(e.elev_status || "").toLowerCase();
      if (statusFilter === "alle") { /* no-op */ }
      else if (statusFilter === "til_eksamen_naeste") {
        // Filtrer til elever hvis expected_exam matcher næste eksamensmåned
        if (!eksamenMaaned || e.expected_exam !== eksamenMaaned) return false;
      } else if (statusFilter === "Tilmeldt Uddannelse") {
        if (!tilmeldtLower.includes(statusLower)) return false;
      } else if (statusFilter === "Aktiv") {
        // "Aktiv"-filter = Aktiv ELLER Tilmeldt Uddannelse (matcher KPI-definition)
        if (!aktivLower.includes(statusLower)) return false;
      } else if (!matchStatus(e.elev_status, statusFilter)) return false;

      if (search) {
        const q = search.toLowerCase();
        if (!e.navn?.toLowerCase().includes(q) && !e.afdeling?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [eleverByFiltered, statusFilter, search, countsData]);

  // Nulstil paginering når filter ændres
  useEffect(() => { setVisAntal(250); }, [statusFilter, search, byFilter]);

  // Paginerede rækker (kun de første visAntal vises i tabellen)
  const eleverVist = useMemo(
    () => eleverFiltered.slice(0, visAntal),
    [eleverFiltered, visAntal]
  );

  // Rekrut: kanaler + total
  const kanaler = useMemo(() => {
    return (rekrutData?.kanaler ?? [])
      .map(k => ({ ...k, kanal: k.kanal === "Ukendt" ? "Direkte" : k.kanal }))
      .sort((a, b) => b.leads - a.leads);
  }, [rekrutData]);

  const totalBudget = useMemo(() => kanaler.reduce((s, k) => s + (k.budget_mdr ?? 0), 0), [kanaler]);
  const totalMedBudget = useMemo(() => kanaler.filter(k => k.budget_mdr != null).reduce((s, k) => s + k.total, 0), [kanaler]);
  const samletLeadpris = totalMedBudget > 0 ? Math.round(totalBudget / totalMedBudget) : null;
  const totalMedBudgetTilmeldte = useMemo(() => kanaler.filter(k => k.budget_mdr != null).reduce((s, k) => s + k.tilmeldte, 0), [kanaler]);
  const samletTilmeldtPris = totalMedBudgetTilmeldte > 0 ? Math.round(totalBudget / totalMedBudgetTilmeldte) : null;

  // ---------- Render ----------

  // Loading: vis spinner indtil begge er hentet
  const initialLoading = eleverLoading && rekrutLoading && !rekrutData && elever.length === 0;
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        <p className="text-[13px] text-muted-foreground mt-3">Henter...</p>
      </div>
    );
  }

  const subtitle = activeTab === "liste"
    ? "Oversigt over alle elever og deres uddannelsesstatus"
    : "Lead-analyse baseret på \"Hvor kender eleven os fra?\" i Podio";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-16">

        {/* Titel */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight mb-1">Rekruttering</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>

        {/* KPI-kasser */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <KpiCard
            icon={<Users className="w-3.5 h-3.5" />}
            label="Tilmeldte elever"
            value={totalTilmeldte.toLocaleString("da-DK")}
            sub="Aktive + Starter senere"
          />
          <KpiCard
            icon={<GraduationCap className="w-3.5 h-3.5" />}
            label="Aktive elever"
            value={aktiveCount.toLocaleString("da-DK")}
            sub="lige nu i uddannelse"
          />
          <KpiCard
            icon={<Clock className="w-3.5 h-3.5" />}
            label="Starter snart"
            value={starterSnartCount.toLocaleString("da-DK")}
            sub={naesteHoldstart
              ? `kommende elever — næste hold: ${fmtDate(naesteHoldstart)}`
              : "kommende elever"}
          />
          <KpiCard
            icon={<GraduationCap className="w-3.5 h-3.5" />}
            label={countsData?.til_eksamen_naeste?.maaned
              ? `Til eksamen ${fmtDate(countsData.til_eksamen_naeste.maaned)}`
              : "Til eksamen"}
            value={countsData?.til_eksamen_naeste != null
              ? countsData.til_eksamen_naeste.total.toLocaleString("da-DK")
              : "—"}
            sub={countsData?.til_eksamen_naeste
              ? `kos: ${countsData.til_eksamen_naeste.kos ?? 0} · frisør: ${countsData.til_eksamen_naeste.frisoer ?? 0}`
              : "næste eksamen"}
            onClick={() => {
              setStatusFilter(sf => sf === "til_eksamen_naeste" ? "alle" : "til_eksamen_naeste");
              setActiveTab("liste");
            }}
            active={statusFilter === "til_eksamen_naeste"}
          />
          <KpiCard
            icon={<Users className="w-3.5 h-3.5" />}
            label="Pipeline (leads)"
            value={totalLeads.toLocaleString("da-DK")}
            sub="Emne → Klar til kontrakt"
          />
          <KpiCard
            icon={<Percent className="w-3.5 h-3.5" />}
            label="Konvertering"
            value={konvPct != null ? `${konvPct.toFixed(1)} %` : "—"}
            sub={`${totalTilmeldte.toLocaleString("da-DK")} tilmeldte af ${totalLeads.toLocaleString("da-DK")} leads i ${periodeLabel}`}
          />
        </div>

        {/* Faner — fælles SubTabs-skabelon (egen fuld-bredde base-linje). */}
        <SubTabs
          tabs={[
            { key: "liste", label: "Elev-liste" },
            { key: "rekruttering", label: "Rekruttering" },
          ]}
          value={activeTab}
          onChange={key => { setActiveTab(key); setSearchParams(p => { const n = new URLSearchParams(p); n.set("tab", key); return n; }, { replace: true }); }}
          className="mb-4"
        />

        {/* By-filter — egen række under fanerne; kun på Elev-liste. */}
        {activeTab === "liste" && (
          <div className="flex flex-wrap gap-1 mb-6">
            {BYER.map(b => (
              <button
                key={b.id}
                onClick={() => setByFilter(b.id)}
                className={`px-2.5 py-1 rounded-full text-[13px] font-semibold transition-colors ${
                  byFilter === b.id
                    ? "bg-foreground text-background"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        )}

        {/* ----- TAB 1: ELEV-LISTE ----- */}
        {activeTab === "liste" && (
          <>
            {/* Status-filter */}
            <div className="flex flex-wrap gap-2 mb-4">
              {FILTER_GROUPS.map(f => {
                const style = STATUS_STYLES[f.id];
                const isActive = statusFilter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setStatusFilter(f.id)}
                    className={`px-3 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${
                      isActive
                        ? style ? `${style.bg} ${style.text}` : "bg-foreground text-background"
                        : "bg-secondary text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {f.label} ({counts[f.id] ?? 0})
                  </button>
                );
              })}
            </div>

            {/* Søgning */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Søg på navn eller by..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm text-foreground w-full max-w-xs focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
              />
            </div>

            {eleverLoading ? (
              <div className="flex flex-col items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                <p className="text-[13px] text-muted-foreground mt-3">Henter elever...</p>
              </div>
            ) : eleverFiltered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Inbox className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">Ingen elever fundet</p>
              </div>
            ) : (
              <div className="rounded-xl border border-border overflow-hidden">
                {/* Mobil: kort-liste (én elev pr. kort) */}
                <div className="sm:hidden divide-y divide-border">
                  {eleverVist.map((elev) => {
                    const s = STATUS_STYLES[elev.elev_status];
                    return (
                      <div key={elev.app_item_id} className="flex items-start gap-3 p-3">
                        {elev.workspace_file_id ? (
                          <img
                            src={`${API}/podio/image/${elev.workspace_file_id}`}
                            alt={elev.navn}
                            className="w-9 h-9 rounded-full object-cover border border-border shrink-0"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center border border-border shrink-0">
                            <span className="text-[13px] font-semibold text-muted-foreground">
                              {elev.navn?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <button
                            onClick={() => handleImpersonate(elev.navn)}
                            className="flex items-center gap-1 max-w-full text-foreground font-medium hover:text-[#FF2A6D] transition-colors group"
                            title={`Se som ${elev.navn}`}
                          >
                            <span className="truncate">{elev.navn || "—"}</span>
                            <Eye className="w-3 h-3 opacity-60 shrink-0" />
                          </button>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-muted-foreground">
                            <span>{displayCity(elev.afdeling)}</span>
                            <span className="opacity-40">·</span>
                            <span>{fmtDate(elev.start_date)}</span>
                            {elev.varighed_maaneder ? (
                              <>
                                <span className="opacity-40">·</span>
                                <span>{elev.varighed_maaneder} mdr.</span>
                              </>
                            ) : null}
                          </div>
                          <div className="mt-2">
                            <select
                              value={elev.elev_status || ""}
                              disabled={savingId === elev.app_item_id}
                              onChange={e => handleStatusChange(elev.app_item_id, e.target.value)}
                              className={`appearance-none text-[13px] font-semibold rounded-full px-2.5 py-1.5 border-0 focus:outline-none cursor-pointer disabled:opacity-50 ${s ? `${s.bg} ${s.text}` : "bg-secondary text-muted-foreground"}`}
                            >
                              <option value="">Ikke angivet</option>
                              <option value="Tilmeldt Uddannelse">Tilmeldt Uddannelse</option>
                              <option value="Aktiv">Aktiv</option>
                              <option value="Starter senere">Starter senere</option>
                              <option value="Orlov">Orlov</option>
                              <option value="Afsluttet uddannelse">Afsluttet uddannelse</option>
                              <option value="Stoppet før tid">Stoppet før tid</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Desktop: tabel */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-secondary/50">
                        <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider w-10"></th>
                        <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Navn</th>
                        <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">By</th>
                        <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Holdstart</th>
                        <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Varighed</th>
                        <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eleverVist.map((elev, i) => {
                        const s = STATUS_STYLES[elev.elev_status];
                        return (
                          <tr key={elev.app_item_id} className={`border-b border-border hover:bg-secondary/30 transition-colors ${i % 2 === 0 ? "" : "bg-secondary/10"}`}>
                            <td className="py-2 px-4">
                              {elev.workspace_file_id ? (
                                <img
                                  src={`${API}/podio/image/${elev.workspace_file_id}`}
                                  alt={elev.navn}
                                  className="w-8 h-8 rounded-full object-cover border border-border"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center border border-border">
                                  <span className="text-[13px] font-semibold text-muted-foreground">
                                    {elev.navn?.charAt(0)?.toUpperCase() || "?"}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 font-medium">
                              <button
                                onClick={() => handleImpersonate(elev.navn)}
                                className="flex items-center gap-1 text-foreground hover:text-[#FF2A6D] transition-colors group"
                                title={`Se som ${elev.navn}`}
                              >
                                <span>{elev.navn || "—"}</span>
                                <Eye className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
                              </button>
                            </td>
                            <td className="py-3 px-4 text-muted-foreground">{displayCity(elev.afdeling)}</td>
                            <td className="py-3 px-4 text-muted-foreground text-[13px]">{fmtDate(elev.start_date)}</td>
                            <td className="py-3 px-4 text-muted-foreground text-[13px]">
                              {elev.varighed_maaneder ? `${elev.varighed_maaneder} mdr.` : "—"}
                            </td>
                            <td className="py-3 px-4">
                              <select
                                value={elev.elev_status || ""}
                                disabled={savingId === elev.app_item_id}
                                onChange={e => handleStatusChange(elev.app_item_id, e.target.value)}
                                className={`appearance-none text-[13px] font-semibold rounded-full px-2.5 py-1 border-0 focus:outline-none cursor-pointer disabled:opacity-50 ${s ? `${s.bg} ${s.text}` : "bg-secondary text-muted-foreground"}`}
                              >
                                <option value="">Ikke angivet</option>
                                <option value="Tilmeldt Uddannelse">Tilmeldt Uddannelse</option>
                                <option value="Aktiv">Aktiv</option>
                                <option value="Starter senere">Starter senere</option>
                                <option value="Orlov">Orlov</option>
                                <option value="Afsluttet uddannelse">Afsluttet uddannelse</option>
                                <option value="Stoppet før tid">Stoppet før tid</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Paginering: vis flere + tæller */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border bg-secondary/30 text-[13px] text-muted-foreground">
                  <span>Viser {eleverVist.length.toLocaleString("da-DK")} af {eleverFiltered.length.toLocaleString("da-DK")} elever</span>
                  {visAntal < eleverFiltered.length && (
                    <button
                      onClick={() => setVisAntal(n => n + 250)}
                      className="px-3 py-2 sm:py-1.5 rounded-lg bg-foreground text-background font-semibold hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                    >
                      Vis flere ({Math.min(250, eleverFiltered.length - visAntal)})
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ----- TAB 2: REKRUTTERING ----- */}
        {activeTab === "rekruttering" && (
          <>
            {rekrutError ? (
              <div>
                <p className="text-destructive text-sm">{rekrutError}</p>
                <button
                  onClick={() => loadRekrut(fra, til)}
                  className="mt-4 border border-border bg-background text-sm font-medium px-4 py-2 rounded-xl hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                >
                  Prøv igen
                </button>
              </div>
            ) : (
              <>
                {/* Periode-filter */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {QUICK_PERIODS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => applyQuick(p)}
                      className={`px-3 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${activeQuick === p.label ? "bg-foreground text-background" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
                    >
                      {p.label}
                    </button>
                  ))}
                  <div className="flex w-full sm:w-auto items-center gap-2 mt-2 sm:mt-0 sm:ml-2 sm:pl-2 sm:border-l border-border">
                    <input
                      type="date"
                      value={fra}
                      onChange={e => { setFra(e.target.value); setActiveQuick(""); }}
                      className="h-9 sm:h-8 min-w-0 flex-1 sm:flex-none px-2 text-[13px] rounded-lg border border-border bg-secondary text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                    />
                    <span className="text-[13px] text-muted-foreground">—</span>
                    <input
                      type="date"
                      value={til}
                      onChange={e => { setTil(e.target.value); setActiveQuick(""); }}
                      className="h-9 sm:h-8 min-w-0 flex-1 sm:flex-none px-2 text-[13px] rounded-lg border border-border bg-secondary text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
                    />
                    <button
                      onClick={applyCustom}
                      className="h-9 sm:h-8 px-3 text-[13px] rounded-lg bg-[#FF2A6D] text-white font-semibold hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 shrink-0"
                    >
                      Søg
                    </button>
                  </div>
                  {rekrutData?.fra && (
                    <span className="text-[13px] text-muted-foreground ml-1">
                      {rekrutData.fra} → {rekrutData.til || "i dag"}
                    </span>
                  )}
                </div>

                {/* Rekrut-specifikke ekstra KPI (leadpris/pris pr. tilmeldt) */}
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 mb-6">
                  <KpiCard
                    icon={<TrendingDown className="w-3.5 h-3.5" />}
                    label="Samlet leadpris"
                    value={samletLeadpris != null ? fmt(samletLeadpris) : "—"}
                    sub={totalBudget > 0 ? `${fmt(totalBudget)}/mdr. budget` : "Sæt budget på kanaler"}
                  />
                  <KpiCard
                    icon={<TrendingDown className="w-3.5 h-3.5" />}
                    label="Pris pr. tilmeldt"
                    value={samletTilmeldtPris != null ? fmt(samletTilmeldtPris) : "—"}
                    sub="Baseret på kanaler m. budget"
                  />
                </div>

                {rekrutLoading ? (
                  <div className="flex flex-col items-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    <p className="text-[13px] text-muted-foreground mt-3">Henter rekrutteringsdata...</p>
                  </div>
                ) : (
                  <>
                    {/* Kanal-tabel */}
                    <div className="rounded-xl border border-border overflow-hidden">
                      <div className="px-4 py-3 border-b border-border bg-secondary/40">
                        <h3 className="text-sm font-semibold text-foreground">Kanal-oversigt</h3>
                        <p className="text-[13px] text-muted-foreground mt-0.5">Klik på budgetbeløbet for at redigere månedligt spend</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-sm">
                          <thead>
                            <tr className="border-b border-border bg-secondary/30">
                              <th className="text-left px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Kanal</th>
                              <th className="text-right px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Leads</th>
                              <th className="text-right px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Tilmeldte</th>
                              <th className="text-right px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Konv.rate</th>
                              <th className="text-right px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Budget/mdr.</th>
                              <th className="text-right px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Leadpris</th>
                              <th className="text-right px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Pris pr. tilmeldt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kanaler.map(k => (
                              <tr key={k.kanal} className="border-b border-border hover:bg-secondary/40 transition-colors">
                                <td className="px-4 py-3 font-medium text-foreground">{k.kanal}</td>
                                <td className="px-4 py-3 text-right text-muted-foreground">{k.leads.toLocaleString("da-DK")}</td>
                                <td className="px-4 py-3 text-right text-muted-foreground">{k.tilmeldte.toLocaleString("da-DK")}</td>
                                <td className="px-4 py-3 text-right text-foreground font-medium">
                                  {k.total > 0 ? (k.tilmeldte / k.total * 100).toFixed(1) + " %" : "—"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <BudgetEditor
                                    kanal={k.kanal}
                                    current={k.budget_mdr}
                                    onSave={v => saveBudget(k.kanal, v)}
                                  />
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {k.leadpris != null
                                    ? <span className="font-medium text-amber-500">{fmt(k.leadpris)}</span>
                                    : <span className="text-muted-foreground/50 text-[13px] italic">—</span>
                                  }
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {k.tilmeldt_pris != null
                                    ? <span className="font-semibold text-emerald-500">{fmt(k.tilmeldt_pris)}</span>
                                    : <span className="text-muted-foreground/50 text-[13px] italic">—</span>
                                  }
                                </td>
                              </tr>
                            ))}
                            {kanaler.length === 0 && (
                              <tr>
                                <td colSpan={7} className="px-4 py-12">
                                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                                    <Inbox className="w-10 h-10 mb-3 opacity-30" />
                                    <p className="text-sm">Ingen data</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                          {totalBudget > 0 && (
                            <tfoot>
                              <tr className="border-t-2 border-border bg-secondary/20">
                                <td className="px-4 py-3 text-[13px] font-bold text-foreground uppercase tracking-wide">Total</td>
                                <td className="px-4 py-3 text-right text-foreground font-medium">
                                  {kanaler.reduce((s, k) => s + k.leads, 0).toLocaleString("da-DK")}
                                </td>
                                <td className="px-4 py-3 text-right text-foreground font-medium">
                                  {kanaler.reduce((s, k) => s + k.tilmeldte, 0).toLocaleString("da-DK")}
                                </td>
                                <td className="px-4 py-3 text-right text-foreground font-bold">
                                  {(() => { const t = kanaler.reduce((s, k) => s + k.total, 0); const ti = kanaler.reduce((s, k) => s + k.tilmeldte, 0); return t > 0 ? (ti / t * 100).toFixed(1) + " %" : "—"; })()}
                                </td>
                                <td className="px-4 py-3 text-right font-bold text-foreground">{fmt(totalBudget)}</td>
                                <td className="px-4 py-3 text-right font-bold text-amber-500">
                                  {samletLeadpris != null ? fmt(samletLeadpris) : "—"}
                                </td>
                                <td className="px-4 py-3 text-right font-bold text-emerald-500">
                                  {samletTilmeldtPris != null ? fmt(samletTilmeldtPris) : "—"}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>

                    <p className="text-[12px] text-muted-foreground/60 mt-4">
                      Leadpris = månedligt budget / antal (leads + tilmeldte). Pris pr. tilmeldt = månedligt budget / antal tilmeldte.<br/>
                      Data hentes live fra Podio. Budgetter gemmes i databasen og kan redigeres ved at klikke på beløbet.
                    </p>
                  </>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
